import { initTheme } from "@/distributions/consumer/y/theme";

import {
  IChannelConfigs,
  WorldmcFeaturedHotel,
  WorldmcPackageContent
} from "../types";
import WorldmcFooter from "./components/WorldmcFooter.vue";
import WorldmcHeader from "./components/WorldmcHeader.vue";
import WorldmcHome from "./components/WorldmcHome.vue";

// 蓝湖切图统一落仓库 assets/worldmc/（首页 Hero、套餐图、车辆图等）
const worldmcAsset = (name: string) =>
  new URL(`../../assets/worldmc/${name}`, import.meta.url).href;

const packageContent: Record<string, WorldmcPackageContent> = {
  1: {
    category: { en: "CITY CONTRASTS", zh: "城市对比" },
    title: { en: "Shanghai Clash of Eras", zh: "上海：时代碰撞" },
    subtitle: {
      en: "Skyline, Secrets & Imperial Dining",
      zh: "天际线、隐秘之地与宫廷盛宴"
    },
    priceUsd: "USD 408",
    priceCny: "CNY 2,769",
    hero: worldmcAsset("pkg-hero-1.jpg"),
    image: worldmcAsset("pkg1-gallery-3.jpg"),
    thumb: worldmcAsset("pkg-thumb-1.jpg"),
    date: "March/April 2027",
    hook: {
      title: {
        en: "Skyline Views + Hidden Shopping",
        zh: "天际线景观 + 隐秘购物"
      },
      desc: {
        en: "Oriental Pearl tower, Asia's best replica market (we negotiate), imperial-themed lunch.",
        zh: "东方明珠塔、亚洲最佳高仿市场（由我们代为议价）、宫廷主题午餐。"
      }
    },
    hookImages: [
      worldmcAsset("pkg1-gallery-1.jpg"),
      worldmcAsset("pkg1-gallery-2.jpg")
    ],
    itineraryImage: worldmcAsset("pkg1-gallery-3.jpg"),
    itinerary: [
      {
        title: { en: "Oriental Pearl Tower", zh: "东方明珠塔" },
        desc: {
          en: "263m observation deck with glass floor walkway. Watch the city unfold below your feet — thrilling views of Shanghai's skyline.",
          zh: "263 米观光层，脚下即是玻璃栈道。俯瞰城市在脚下展开——令人心跳的上海天际线景观。"
        }
      },
      {
        title: { en: "Asia Pacific Sourcing", zh: "亚太采购市场" },
        desc: {
          en: "The best replica market in Asia. Bags, watches, leather. Negotiation is must.",
          zh: "亚洲最好的高仿市场。箱包、腕表、皮具，议价是必修课。"
        }
      },
      {
        title: { en: "Palace Banquet Lunch", zh: "宫廷宴午餐" },
        desc: {
          en: "Imperial Chinese theme with live performance, creative cuisine. Dinner as theater — an unforgettable culinary spectacle.",
          zh: "中式宫廷主题，配现场演出与创意料理。一餐如一场戏剧——难忘的美食盛宴。"
        }
      }
    ],
    included: [
      { en: "Private premium vehicle", zh: "私人高级用车" },
      { en: "Oriental Pearl tickets", zh: "东方明珠门票" },
      { en: "English Speaking Guide", zh: "英文导游" },
      { en: "Palace Lunch Banquet", zh: "宫廷宴午餐" }
    ],
    note: { en: "*Beverage on own", zh: "*饮料自理" },
    extra: {
      en: "Alternative: Prefer authentic luxury brands? We substitute Plaza 66 instead of market.",
      zh: "备选：更偏好正品奢侈品？我们将以恒隆广场 66 替代市场行程。"
    }
  },
  2: {
    category: { en: "CITY ESSENTIALS", zh: "城市经典" },
    title: { en: "Shanghai Icons in One Day", zh: "一日看尽上海地标" },
    subtitle: {
      en: "Efficient, Photogenic, Zero Wasted Time",
      zh: "高效、出片、不浪费时间"
    },
    priceUsd: "USD 248",
    priceCny: "CNY 1,684",
    hero: worldmcAsset("pkg-hero-2.jpg"),
    image: worldmcAsset("pkg2-gallery-3.jpg"),
    thumb: worldmcAsset("pkg-thumb-2.jpg"),
    date: "March/April 2027",
    hook: {
      title: { en: "Shanghai's Greatest Hits", zh: "上海必游精选" },
      desc: {
        en: "Bund skyline, Yu Garden, French Concession. Efficient, photogenic, zero wasted time.",
        zh: "外滩天际线、豫园、法租界。高效、出片、不浪费时间。"
      }
    },
    hookImages: [
      worldmcAsset("pkg2-gallery-1.jpg"),
      worldmcAsset("pkg2-gallery-2.jpg")
    ],
    itineraryImage: worldmcAsset("pkg2-gallery-3.jpg"),
    itinerary: [
      {
        title: { en: "The Bund", zh: "外滩" },
        desc: {
          en: "Colonial architecture vs. Lujiazui skyscrapers. The classic photo op where history meets futurism across the Huangpu River.",
          zh: "殖民建筑对望陆家嘴摩天楼。黄浦江两岸，历史与未来同框的经典机位。"
        }
      },
      {
        title: { en: "Yu Garden", zh: "豫园" },
        desc: {
          en: "400-year-old Ming garden with dragon walls, zigzag bridge. Exit through Chenghuang Temple snack street for local delicacies.",
          zh: "四百年明代园林，龙墙与九曲桥。出园即达城隍庙小吃街，品尝本地美味。"
        }
      },
      {
        title: { en: "French Concession", zh: "法租界" },
        desc: {
          en: "Plane trees, 1920s villas, coffee shops. Feels like Paris, is Shanghai. A stroll through the city's most elegant neighborhood.",
          zh: "梧桐树荫、上世纪别墅与咖啡馆。恍如巴黎，却是上海——漫步城市最优雅的街区。"
        }
      }
    ],
    included: [
      { en: "Private premium vehicle", zh: "私人高级用车" },
      { en: "Yu Garden ticket", zh: "豫园门票" },
      { en: "English Speaking Guide", zh: "英文导游" }
    ],
    note: {
      en: "*Lunch & Beverage on own/Guide will give good lunch suggestion",
      zh: "*午餐与饮料自理/导游将推荐优质午餐"
    },
    extra: {
      en: "Physical level: 8K-10K steps. Wear walking shoes.",
      zh: "体力要求：8K-10K 步，请穿舒适的步行鞋。"
    }
  },
  3: {
    category: { en: "PRIVATE HERITAGE", zh: "私享人文" },
    title: { en: "Zhujiajiao Private Heritage Day", zh: "朱家角私享人文一日" },
    subtitle: {
      en: "A Journey Through Time",
      zh: "一段穿越时光的旅程"
    },
    priceUsd: "USD 384",
    priceCny: "CNY 2,607",
    hero: worldmcAsset("pkg-hero-3.jpg"),
    image: worldmcAsset("pkg3-gallery-3.jpg"),
    thumb: worldmcAsset("pkg-thumb-3.jpg"),
    date: "March/April 2027",
    hook: {
      title: { en: "Ancient Town-Water Village", zh: "古镇水乡" },
      desc: {
        en: "Ming & Qing Dynasty residence, hand-rowed canal boat, Song Dynasty tea ritual. Not tourist-packed Zhujiajiao.",
        zh: "明清宅邸、手划船游巷、宋代茶礼。不是人潮拥挤的朱家角。"
      }
    },
    sectionLabel: {
      en: "Part 1 · A Journey Through Time",
      zh: "第一部分 · 穿越时光的旅程"
    },
    hookImages: [
      worldmcAsset("pkg3-gallery-1.jpg"),
      worldmcAsset("pkg3-gallery-2.jpg")
    ],
    /* CDEH-63828 二轮：三图按蓝湖画板对号——行程大图=水巷（4）、叠加小图=茶艺师（5）、Part2 左图=牌楼（3） */
    itineraryImage: worldmcAsset("pkg3-gallery-4.jpg"),
    itineraryOverlayImage: worldmcAsset("pkg3-gallery-5.jpg"),
    itinerary: [
      {
        title: { en: "Hand-Rowed Canal Boat", zh: "手划船水巷游" },
        desc: {
          en: "15-minute journey through Ming Dynasty canals and stone bridges. No motor noise — just the gentle splash of oars and whispers of history.",
          zh: "15 分钟穿行明代水巷与石桥。没有马达声——只有船桨轻划与历史的低语。"
        }
      },
      {
        title: { en: "Song Dynasty Tea Ceremony", zh: "宋代茶礼" },
        desc: {
          en: "At Ahn Luh Zhujiajiao: Grind tea leaves, whisk into art, drink from Song-era recipe. Experience the elegant ritual that influenced Japanese tea culture.",
          zh: "于安麓朱家角：碾茶、击拂成艺、依宋方品饮。体验影响日本茶道的风雅仪式。"
        }
      },
      {
        title: {
          en: "Farmhouse Lunch-Guide give lunch suggestion, expense on own",
          zh: "农家午餐——导游提供推荐，费用自理"
        },
        desc: {
          en: "Local lake fish, braised pork, bamboo shoots — authentic home-style cuisine prepared with recipes passed down through generations.",
          zh: "本地湖鲜、红烧肉、竹笋——世代相传的家常味道。"
        }
      }
    ],
    quote: {
      en: "This isn't a hotel — it's a time machine with heated floors.",
      zh: "这不是一家酒店——这是一台带地暖的时光机。"
    },
    part2: {
      label: { en: "Part 2 · Ahn Luh Zhujiajiao", zh: "第二部分 · 安麓朱家角" },
      subtitle: {
        en: "A Time Machine with Heated Floors",
        zh: "一台带地暖的时光机"
      },
      title: {
        en: "Song Dynasty Tea Ceremony at 600-year-old Ming Dynasty mansion.",
        zh: "于 600 年历史的明代宅邸，体验宋代茶礼。"
      },
      desc: {
        en: "Malaysian architect restored original wood beams, and opera stage.",
        zh: "马来西亚建筑师修复了原木梁柱与古戏台。"
      },
      image: worldmcAsset("pkg3-gallery-3.jpg")
    },
    included: [
      { en: "Private premium vehicle", zh: "私人高级用车" },
      { en: "Exclusive boat charter", zh: "专属包船" },
      { en: "Tea master + ceremony", zh: "茶艺师 + 茶礼" },
      { en: "English Speaking Guide", zh: "英文导游" }
    ],
    note: {
      en: "*Based on 2-person minimum • Lunch & Beverage on own",
      zh: "*2 人起订 · 午餐与饮料自理"
    }
  }
};

// 官方推荐酒店：固定写死三家（TB CDEH-63834 北外滩酒店清单，价格按 EUR 固定）；
// 图片暂用 Unsplash 占位，待 P-05 正式设计切图替换
const featuredHotels: WorldmcFeaturedHotel[] = [
  {
    hotelId: "5b35ddaf476ef1db61f7bb0d",
    image:
      "https://qiniu-uploads-cdn.charmdeer.com/FgG1zSnN0KycfCb1IyDI2gzQAimq",
    name: "Hyatt on the Bund, Shanghai",
    brand: "Hyatt",
    stars: 5,
    room: "1 King / 2 Twin Beds · 42-45m²",
    distance: "740m to Hong Space · 11 minutes by walking",
    portalPrice: "EUR 155",
    portalPriceNote: "/ room night average · with 1-2 breakfast",
    bar: ["1 breakfast: EUR 224-231 avg", "2 breakfasts: EUR 243-251 avg"]
  },
  {
    hotelId: "5b50768d6a8860a3024dfee9",
    image:
      "https://qiniu-uploads-cdn.charmdeer.com/Fpi4tJ03iDxLBKUqgvSlj4CVz8tE",
    name: "W Shanghai – The Bund",
    brand: "W",
    stars: 5,
    room: "1 King / 2 Twin · City View · 46m²",
    distance: "340m to Hong Space · 6 minutes by walking",
    portalPrice: "EUR 275",
    portalPriceNote: "/ room night average · with 1-2 breakfast",
    bar: ["1 breakfast: EUR 421 avg", "2 breakfasts: EUR 441 avg"]
  },
  {
    hotelId: "5b5077656a8860a3024e0a1f",
    image:
      "https://qiniu-uploads-cdn.charmdeer.com/Frk0U4jRe5rMl_H4kQPTjgiqOkKD",
    name: "Ocean Hotel Shanghai",
    brand: "Ocean Hotel",
    stars: 4,
    room: "1 King / 2 Twin Beds · 56m²",
    distance: "1400m to Hong Space · 22 minutes by walking",
    portalPrice: "EUR 115",
    portalPriceNote: "/ room night average · with 1-2 breakfast",
    bar: ["1 breakfast: EUR 115 avg", "2 breakfasts: EUR 127 avg"]
  }
];

export default {
  configs: {
    key: "worldmc",
    // 默认浏览币种 EUR（CDEH-64495）；渠道币种/支付币种仍为 USD（服务端渠道配置）
    currency: "EUR",
    languages: [
      { label: "English", value: "en" },
      { label: "中文", value: "zh" }
    ],
    navigation: [
      { label: "shell.worldmc.nav.hotels", path: "/index" },
      { label: "shell.worldmc.nav.visa", path: "/visa-information" },
      { label: "shell.worldmc.nav.packages", path: "/travel-packages/1" },
      { label: "shell.worldmc.nav.transfers", path: "/airport-transfers" }
    ],
    useChannelLogo: true,
    useChannelLanguages: true,
    header: {
      logoAlign: "left",
      showBackground: false
    },
    components: {
      Index: {
        component: WorldmcHome
      },
      // 公共页头部渠道化（Navbar 按 configs.components.Header 配置驱动渲染，
      // 契约：接收 showBreadcrumb / routes props）
      Header: {
        component: WorldmcHeader
      },
      // 公共页（订单列表/详情等）Footer 渠道化（A-03 渠道视图解析）
      Footer: {
        component: WorldmcFooter
      }
    },
    pages: {
      login: {
        bannerImageUrl: worldmcAsset("login-kv.jpg")
      },
      prepareOrder: {
        stripePay: true,
        // 码头大会无套餐业务，隐藏下单页「套餐」入口（CDEH-64494）
        showVoucher: false
      },
      worldmc: {
        packages: packageContent,
        featuredHotels,
        // 上海城市 ref（接送机服务城市回退、首页 More Stays 按城市查询酒店的地点）
        // 与城市名（aMap 联想/机场列表/首页跳转列表页 showLabel 兜底）
        carCityRef: "59ad44a9c8ae8686cd67611c",
        carCityName: "上海"
      }
    }
  },
  init() {
    initTheme();
  }
} as IChannelConfigs;
